import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { AcornsIcon } from '@acorns/icons'
import { useRemoteStrings } from '@acorns/web-utils'
import { useMutation } from '@apollo/react-hooks'
import PinwheelModal, { PinwheelError } from '@pinwheel/react-modal'

import pipeLander from 'assets/images/spend/direct-deposit/lander-pipe.svg'
import { BankCardTier, BankType } from 'generated/types'
import { routes } from 'router/routes'
import themeValues from 'theme'
import { GET_DIRECT_DEPOSIT_FORM_BUTTON } from 'utils/testing/testIDs'

import { landerCTATap } from '../../direct-deposit-form/segment'
import { DisclosureDrawer } from './drawers/DisclosureDrawer'
import { SetUpDrawer } from './drawers/SetUpDrawer'
import CreateDDLinkInfo from './getPinwheelToken.gql'
import {
  ContentContainer,
  DisclaimerContainer,
  DownloadButton,
  EarlyPaydayDisclaimerText,
  EarlyPaydayIconContainer,
  EarlyPaydayImg,
  StyledCheckmark,
  StyledDirectDepositCard,
  StyledHeadingEarlyPayday,
  StyledIncentiveContainer,
  StyledIncentiveRow,
  StyledUtilityArrowRight,
} from './InterstitialComponents'
import { Props } from './SearchComponents'
import {
  disclosureCTA,
  disclosureExit,
  payrollError,
  pinWheelModal,
  pinWheelSuccess,
} from './segment'

export enum DirectDepositMethod {
  Pinwheel = 'PINWHEEL',
  Manual = 'MANUAL',
}

export const InterstitialContent: React.FC<Props> = (props: Props) => {
  const history = useHistory()
  const { getRemoteString, StyledRemoteString } = useRemoteStrings()
  const [showSetUpDrawer, setShowSetUpDrawer] = useState(false)
  const [showDisclosureDrawer, setShowDisclosureDrawer] = useState(false)
  const [showPinwheelModal, setShowPinwheelModal] = useState(false)
  const [pinwheelToken, setPinwheelToken] = useState('')
  const isMOC =
    props.data?.spendAccount?.bankCardOrder?.bankCardTier ===
    BankCardTier.MightyOak

  const [
    getPinwheelToken,
    { data: createDDLinkData, loading: isLoadingDDLinkData },
  ] = useMutation(CreateDDLinkInfo)

  useEffect(() => {
    if (createDDLinkData?.createDirectDepositLinkInfo?.results?.token) {
      setPinwheelToken(
        createDDLinkData?.createDirectDepositLinkInfo?.results?.token,
      )
      setShowPinwheelModal(true)
      props.analytics.track('Event Occurred', pinWheelModal())
      closeDisclosureDrawer()
    }
  }, [createDDLinkData])

  const isEmergencyFundEligible =
    props?.data?.spendAccounts &&
    props.data.spendAccounts[0].bankType === BankType.Nbkc

  const closeSetUpDrawer = () => {
    setShowSetUpDrawer(false)
  }

  const closeDisclosureDrawer = () => {
    props.analytics.track('Button Tapped', disclosureExit())
    setShowDisclosureDrawer(false)
  }

  const disclosureDrawerBackButtonPress = () => {
    props.analytics.track(
      'Button Tapped',
      disclosureCTA(
        getRemoteString({
          key: 'spend.direct_deposit.drawer.terms.cta.secondary',
        }),
      ),
    )
    setShowSetUpDrawer(true)
    setShowDisclosureDrawer(false)
  }

  const goToPinwheel = () => {
    props.analytics.track(
      'Button Tapped',
      disclosureCTA(
        getRemoteString({
          key: 'spend.direct_deposit.drawer.terms.cta.primary',
        }),
      ),
    )
    getPinwheelToken({
      variables: {
        userId: props.data?.user?.uuid,
        accountId: props.bankAccountId,
      },
    })
  }

  const onPinwheelSuccess = () => {
    props.analytics.track('Event Occurred', pinWheelSuccess())
    if (isEmergencyFundEligible) {
      return history.push(routes.spend.directDepositSuccess)
    }
    history.push(routes.spend.index)
  }

  const onPinwheelExit = () => {
    setShowPinwheelModal(false)
  }

  const onPinwheelError = (e: PinwheelError) => {
    props.analytics.track('Screen Viewed', payrollError(e.code))
  }

  const goToDirectDepositForm = () => {
    props.analytics.track('Button Tapped', landerCTATap('Get deposit form'))
    history.push(routes.spend.directDepositEmployerInfoForm)
  }

  const onMainButtonClick = () => {
    setShowSetUpDrawer(true)
  }

  const goToEarlyPaydayTerms = () => {
    const articleId = getRemoteString({
      key: 'spend.early_payday.terms.article_id',
    })
    return `/grow/${articleId}`
  }

  const getTitle = () => {
    return (
      <StyledHeadingEarlyPayday>
        {getRemoteString({
          key: 'spend.direct_deposit.landing.header.early_payday_only',
        })}
      </StyledHeadingEarlyPayday>
    )
  }

  const getBulletPoints = () => {
    const bulletPointsArray = [
      {
        id: 'smart_deposit_point',
        element: (
          <div>
            {getRemoteString({
              key: 'spend.direct_deposit.landing.highlight.smart_deposit_v2',
            })}
          </div>
        ),
      },
      {
        id: 'early_payday',
        element: (
          <div>
            {getRemoteString({
              key: 'banking.direct_deposit.landing.benefits.early_payday.no_superscript',
            })}
            <sup> {isMOC ? 2 : 1}</sup>
          </div>
        ),
      },
    ]
    if (!isMOC) {
      bulletPointsArray.unshift({
        id: 'pinwheel_point',
        element: (
          <div>
            {getRemoteString({
              key: 'spend.direct_deposit.landing.highlight.payroll_provider',
            })}
          </div>
        ),
      })
    }
    if (isEmergencyFundEligible) {
      bulletPointsArray.push({
        id: 'emergency_fund_point',
        element: (
          <div>
            {getRemoteString({
              key: 'spend.direct_deposit.landing.highlight.emergency_fund',
            })}
          </div>
        ),
      })
    }

    return (
      <StyledIncentiveContainer>
        {bulletPointsArray.map((bulletPointObject) => (
          <StyledIncentiveRow key={bulletPointObject.id}>
            <EarlyPaydayIconContainer>
              <StyledCheckmark
                icon={AcornsIcon.Icon.Checkmark}
                width="15px"
                height="11px"
              />
            </EarlyPaydayIconContainer>
            {bulletPointObject.element}
          </StyledIncentiveRow>
        ))}
      </StyledIncentiveContainer>
    )
  }

  const getFooter = () => {
    const disclaimsArray = [
      {
        id: 'early-payday',
        element: (
          <StyledRemoteString
            modifiers={[
              {
                bold: 600,
                color: themeValues.colors.green,
                underlined: false,
              },
            ]}
            stringKey="banking.direct_deposit.landing.footnote.early_payday.no_superscript.markdown"
            variables={[goToEarlyPaydayTerms()]}
          />
        ),
      },
    ]
    if (isMOC) {
      disclaimsArray.unshift({
        id: 'terms',
        element: (
          <StyledRemoteString
            stringKey="banking.direct_deposit.landing.footnote.terms.markdown"
            modifiers={[
              {
                bold: 600,
                color: themeValues.colors.green,
                underlined: false,
              },
            ]}
          />
        ),
      })
    }

    return (
      <DisclaimerContainer>
        <EarlyPaydayDisclaimerText>
          {getRemoteString({
            key: 'banking.direct_deposit.landing.footnote2',
          })}
        </EarlyPaydayDisclaimerText>
        {disclaimsArray.map((disclaim, index) => (
          <EarlyPaydayDisclaimerText key={disclaim.id}>
            <sup>{index + 1}</sup> {disclaim.element}
          </EarlyPaydayDisclaimerText>
        ))}
      </DisclaimerContainer>
    )
  }

  return (
    <>
      <StyledDirectDepositCard>
        <ContentContainer>
          {getTitle()}
          {getBulletPoints()}
          <DownloadButton
            onPress={onMainButtonClick}
            data-testid={GET_DIRECT_DEPOSIT_FORM_BUTTON}
          >
            <span>
              {getRemoteString({
                key: 'spend.direct_deposit.landing.cta.get_started',
              })}
            </span>
            <StyledUtilityArrowRight
              icon={AcornsIcon.Icon.UtilityArrowRight}
              width="24px"
              height="24px"
            />
          </DownloadButton>
          {getFooter()}
          <EarlyPaydayImg src={pipeLander} />
        </ContentContainer>
      </StyledDirectDepositCard>
      <SetUpDrawer
        showSetUpDrawer={showSetUpDrawer}
        closeSetUpDrawer={closeSetUpDrawer}
        setShowDisclosureDrawer={setShowDisclosureDrawer}
        goToDirectDepositForm={goToDirectDepositForm}
      />
      <DisclosureDrawer
        showDisclosureDrawer={showDisclosureDrawer}
        closeDisclosureDrawer={closeDisclosureDrawer}
        disclosureDrawerBackButtonPress={disclosureDrawerBackButtonPress}
        goToPinwheel={goToPinwheel}
        isLoadingDDLinkData={isLoadingDDLinkData}
      />
      <PinwheelModal
        linkToken={pinwheelToken}
        open={showPinwheelModal}
        onSuccess={onPinwheelSuccess}
        onExit={onPinwheelExit}
        onError={onPinwheelError}
      />
    </>
  )
}
